import type { ResetRedisCacheDto } from "@repo/types";
import apiClient from "../lib/apiClient";

/**
 * Redis 초기화
 * POST /api/admin/system/redis/reset
 *
 * 백엔드 응답 구조:
 * - 성공 (200): { statusCode: 200, data: { success, message, flushedKeysCount, executionTime } }
 * - 실패 (401, 403, 500): { statusCode, message, error }
 *
 * @param password - 관리자 현재 비밀번호 (재검증용)
 * @returns 초기화 결과 및 통계 정보
 */
export const resetRedisAccessTokens = async (
  password: string
): Promise<{
  success: boolean;
  message: string;
  flushedKeysCount: number;
  executionTime: number;
}> => {
  try {
    const dto: ResetRedisCacheDto = { password };
    const response = await apiClient.post<{
      statusCode?: number;
      data?: {
        success: boolean;
        message: string;
        flushedKeysCount: number;
        executionTime: number;
      };
      message?: string;
      error?: string;
    }>("/admin/system/redis/reset", dto);

    console.log("📦 Redis 초기화 응답:", {
      status: response.status,
      data: response.data,
      dataType: typeof response.data,
      dataKeys: response.data ? Object.keys(response.data) : [],
    });

    // 성공 응답 처리 (HTTP 상태 코드 200 또는 201)
    if (response.status === 200 || response.status === 201) {
      if (response.data && typeof response.data === "object") {
        const responseData = response.data as any;

        // 경우 1: { statusCode: 200, data: { ... } } 구조
        if (responseData.statusCode && responseData.data) {
          if (
            responseData.statusCode === 200 &&
            "flushedKeysCount" in responseData.data
          ) {
            return responseData.data;
          }
        }

        // 경우 2: { success, message, flushedKeysCount, executionTime } 구조 (직접 데이터)
        if (
          "flushedKeysCount" in responseData &&
          "executionTime" in responseData
        ) {
          return {
            success: responseData.success ?? true,
            message: responseData.message ?? "Redis 초기화 완료",
            flushedKeysCount: responseData.flushedKeysCount,
            executionTime: responseData.executionTime,
          };
        }
      }
    }

    // 예상치 못한 응답 구조
    console.error("❌ 예상치 못한 응답 구조:", response.data);
    throw new Error("예상치 못한 응답 구조입니다.");
  } catch (error: unknown) {
    // 에러 응답 처리
    if (error && typeof error === "object" && "response" in error) {
      const axiosError = error as {
        response?: {
          status?: number;
          data?: {
            statusCode?: number;
            message?: string;
            error?: string;
          };
        };
      };

      if (axiosError.response?.data) {
        const errorData = axiosError.response.data;
        const errorMessage =
          errorData.error ||
          errorData.message ||
          "알 수 없는 오류가 발생했습니다.";

        // 에러 객체 생성하여 throw
        const customError = new Error(errorMessage) as Error & {
          statusCode?: number;
          response?: { data?: { error?: string; message?: string } };
        };
        customError.statusCode = axiosError.response.status;
        customError.response = axiosError.response;
        throw customError;
      }
    }

    // 기타 에러
    throw error;
  }
};
